import React, { useState, useEffect, useRef } from 'react';
import { CloseIcon, HeartIcon, CartIcon } from './Icons';
import MenuDrawer from './MenuDrawer';
import AuthModal from './AuthModal';
import Footer from './Footer';
import Header from './Header';
import { triggerStockLimitAlert, subscribeReviews, uploadImage, saveReview, resolveMediaUrl, getProductTimestamp } from '../utils/firebaseService';

// ─── Star rating helper ───────────────────────────────────────────────────────
const Stars = ({ rating = 5, size = 'sm' }) => {
  const sz = size === 'sm' ? 'w-3.5 h-3.5' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => (
        <svg key={i} className={`${sz} ${i <= rating ? 'text-amber-500' : 'text-[#584738]/20'}`} fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
};

// ─── Rating bar ──────────────────────────────────────────────────────────────
const RatingBar = ({ label, pct }) => (
  <div className="flex items-center gap-3 text-[11px] font-sans text-[#584738]/70">
    <span className="w-12 text-right shrink-0">{label}</span>
    <div className="flex-1 h-1.5 bg-[#584738]/10 rounded-full overflow-hidden">
      <div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }} />
    </div>
  </div>
);

// ─── Review card ─────────────────────────────────────────────────────────────
const ReviewCard = ({ name, date, text, rating = 5, images = [], onImageClick }) => (
  <div className="border-b border-[#584738]/10 py-6">
    <div className="flex items-start justify-between gap-4 mb-3">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-[#584738]/15 flex items-center justify-center text-[#584738] font-serif font-semibold text-sm shrink-0">
          {name[0]}
        </div>
        <div>
          <p className="text-sm font-sans font-semibold text-[#584738]">{name}</p>
          <p className="text-[10px] font-sans text-[#584738]/50 tracking-wider">Verified Buyer</p>
        </div>
      </div>
      <span className="text-[11px] text-[#584738]/45 font-sans shrink-0">{date}</span>
    </div>
    <p className="text-[13px] font-sans text-[#584738]/75 leading-relaxed mb-3">{text}</p>
    <div className="flex items-center gap-3 mb-3">
      <Stars rating={rating} />
      <span className="text-[11px] text-amber-500 font-sans font-semibold">{rating}.0</span>
    </div>
    {images.length > 0 && (
      <div className="flex gap-2 mt-2">
        {images.map((src, i) => (
          <img
            key={i}
            src={src}
            alt=""
            onClick={() => onImageClick && onImageClick(src)}
            className="w-14 h-14 object-cover border border-[#584738]/10 cursor-zoom-in hover:opacity-85 transition-opacity duration-200 hover:scale-105 transform"
          />
        ))}
      </div>
    )}
  </div>
);

// Expanded reviews list for "More Reviews" modal page
const ALL_REVIEWS_DATA = [];

// ─── Main Component ───────────────────────────────────────────────────────────
export default function NewArrivals({
  view, setView,
  isMenuOpen, setIsMenuOpen,
  isCartOpen, setIsCartOpen,
  isBookingOpen, setIsBookingOpen,
  isSearchOpen, setIsSearchOpen,
  favorites, setFavorites,
  cartItems, setCartItems,
  bookingStep, setBookingStep,
  bookingForm, setBookingForm,
  currentUser,
  setCurrentUser,
  products = [],
  wishlist = [],
  toggleWishlist,
  setSelectedProduct,
}) {
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const sortedArrivals = React.useMemo(() => {
    const arrivals = (products || []).filter(p => p.isNewArrival || p.category === 'New arrival');
    const list = arrivals.length > 0 ? arrivals : (products || []);
    return [...list].sort((a, b) => getProductTimestamp(b) - getProductTimestamp(a));
  }, [products]);

  const fallbackProd = (products && products.length > 0) ? products[0] : null;
  const product = sortedArrivals[0] || fallbackProd || {
    id: 'placeholder',
    name: 'Coming Soon',
    price: 0,
    image: '',
    description: 'Check back later for our newest additions.',
    additionalInfo: [],
    reviews: [],
  };

  // Gallery images from product's actual uploaded assets, all resolved
  const rawImages = Array.isArray(product.images) && product.images.length > 0
    ? product.images.map(resolveMediaUrl).filter(Boolean)
    : [product.image, product.image2, product.image3, product.image4].map(resolveMediaUrl).filter(Boolean);
  const galleryImages = rawImages.length > 0 ? rawImages : (product.image ? [resolveMediaUrl(product.image)] : []);
  const liveStock = typeof product.stock === 'number' ? product.stock : 10;
  const isOutOfStock = liveStock <= 0;

  const [activeImage, setActiveImage] = useState(0);
  const [qty, setQty] = useState(isOutOfStock ? 0 : 1);
  const [activeTab, setActiveTab] = useState('description');
  const isWishlisted = Boolean(product && (wishlist || []).some(id => String(id) === String(product.id)));
  const [reviewSort, setReviewSort] = useState('latest');
  const [isAllReviewsOpen, setIsAllReviewsOpen] = useState(false);
  const [activeLightboxImage, setActiveLightboxImage] = useState(null);
  
  // Interactive reviews and submission states
  const [reviewsList, setReviewsList] = useState(ALL_REVIEWS_DATA);
  const [visibleReviewsCount, setVisibleReviewsCount] = useState(3);
  const [showWriteForm, setShowWriteForm] = useState(false);
  const [newReviewRating, setNewReviewRating] = useState(5);
  const [newReviewText, setNewReviewText] = useState('');
  const [newReviewName, setNewReviewName] = useState('');
  const [newReviewImages, setNewReviewImages] = useState([]);
  const [isUploadingReviewImages, setIsUploadingReviewImages] = useState(false);
  const [attachMockImage, setAttachMockImage] = useState(false);

  useEffect(() => {
    if (currentUser && !newReviewName) {
      setNewReviewName(currentUser.displayName || currentUser.email?.split('@')[0] || '');
    }
  }, [currentUser, newReviewName]);

  // Live real-time reviews subscription
  useEffect(() => {
    const unsubReviews = subscribeReviews((allReviews) => {
      const productReviews = (allReviews || []).filter(
        r => (r.productId && r.productId === 'na-hero') || 
             (r.product && r.product.toLowerCase().includes('temple nakashi'))
      );
      if (productReviews.length > 0) {
        setReviewsList(productReviews);
      }
    }, ALL_REVIEWS_DATA);

    return () => {
      if (unsubReviews) unsubReviews();
    };
  }, []);

  const handleReviewImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setIsUploadingReviewImages(true);
    const urls = [...newReviewImages];
    for (const file of files) {
      const url = await uploadImage(file);
      if (url) urls.push(url);
    }
    setNewReviewImages(urls);
    setIsUploadingReviewImages(false);
  };

  const handleAddReviewSubmit = async (e) => {
    e.preventDefault();
    if (!newReviewText.trim()) {
      alert("Please enter review remarks.");
      return;
    }
    const finalImages = [...newReviewImages];
    if (attachMockImage) {
      finalImages.push('/blue_pottery_prod_1.jpg');
    }
    const createdReview = {
      name: newReviewName.trim() || 'Valued Patron',
      date: 'Just now',
      rating: newReviewRating,
      text: newReviewText.trim(),
      images: finalImages,
      product: product.name,
      productId: 'na-hero',
      status: 'Pending',
      timestamp: Date.now()
    };

    try {
      await saveReview(createdReview);
      setNewReviewText('');
      setNewReviewName('');
      setNewReviewImages([]);
      setNewReviewRating(5);
      setShowWriteForm(false);
    } catch (err) {
      alert("Failed to submit your review to the database.");
    }
  };

  const getSubtotal = () => cartItems.reduce((acc, item) => acc + item.price * item.qty, 0);

  const handleAddToCart = () => {
    if (isOutOfStock) {
      triggerStockLimitAlert(0);
      return;
    }
    const maxStock = liveStock;
    setCartItems(prev => {
      const existing = prev.find(item => String(item.id) === String(product.id));
      const currentQty = existing ? existing.qty : 0;
      if (currentQty + qty > maxStock) {
        triggerStockLimitAlert(maxStock);
        const available = Math.max(0, maxStock - currentQty);
        if (available === 0) return prev;
        if (existing) {
          return prev.map(item => String(item.id) === String(product.id) ? { ...item, stock: maxStock, qty: maxStock } : item);
        } else {
          return [...prev, { ...product, stock: maxStock, qty: Math.min(qty, maxStock) }];
        }
      }
      if (existing) return prev.map(item => String(item.id) === String(product.id) ? { ...item, stock: maxStock, qty: item.qty + qty } : item);
      return [...prev, { ...product, stock: maxStock, qty }];
    });
  };

  const [undoItem, setUndoItem] = useState(null);
  const undoTimerRef = useRef(null);

  const handleQtyChange = (id, delta) => {
    setCartItems(prev => {
      const item = prev.find(i => String(i.id) === String(id));
      if (!item) return prev;
      if (item.qty + delta <= 0) {
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        setUndoItem(item);
        undoTimerRef.current = setTimeout(() => setUndoItem(null), 5000);
        return prev.filter(i => String(i.id) !== String(id));
      }
      const liveP = (products || []).find(p => String(p.id) === String(id));
      const itemStock = (liveP && typeof liveP.stock === 'number')
        ? liveP.stock
        : ((typeof item.stock === 'number') ? item.stock : 10);
      if (delta > 0 && item.qty + delta > itemStock) {
        triggerStockLimitAlert(itemStock);
        return prev.map(i => i.id === id ? { ...i, qty: itemStock } : i);
      }
      return prev.map(i => i.id === id ? { ...i, qty: i.qty + delta } : i);
    });
  };

  const removeItem = (id) => {
    const item = cartItems.find(i => i.id === id);
    if (item) {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      setUndoItem(item);
      undoTimerRef.current = setTimeout(() => setUndoItem(null), 5000);
    }
    setCartItems(prev => prev.filter(i => i.id !== id));
  };

  const handleUndo = () => {
    if (undoItem) {
      setCartItems(prev => [...prev, { ...undoItem, qty: 1 }]);
      setUndoItem(null);
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    }
  };

  const tabs = ['description', 'additional information', 'reviews'];

  return (
    <div className="bg-noise min-h-screen font-sans text-[#584738] flex flex-col antialiased">

      <Header
        setView={setView}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        setIsSearchOpen={setIsSearchOpen}
        setIsAuthOpen={setIsAuthOpen}
        setIsCartOpen={setIsCartOpen}
        cartItems={cartItems}
      />

      {/* ── MAIN ── */}
      <main className="max-w-7xl mx-auto px-4 md:px-12 py-6 md:py-10 w-full flex-1">

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-[10.5px] font-sans tracking-widest uppercase text-[#584738]/45 mb-4">
          <button onClick={() => setView('home')} className="hover:text-[#584738] transition-colors focus:outline-none">Home</button>
          <span>/</span>
          <span className="text-[#584738]/70">New Arrivals</span>
        </div>

        {/* ── PRODUCT DETAIL GRID ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-16 items-start mb-6">

          {/* LEFT: Gallery — sticky on desktop so it stays in view while tabs scroll */}
          <div className="flex flex-col gap-4 md:sticky md:top-20 md:self-start">
            {/* Main image */}
            <div className="relative overflow-hidden bg-[#FAF8F5] border border-[#584738]/8 aspect-square sm:aspect-[4/5] flex items-center justify-center">
              {galleryImages.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`${product.name} - view ${i + 1}`}
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    e.target.onerror = null;
                    if (galleryImages[1] && e.target.src !== galleryImages[1]) {
                      e.target.src = galleryImages[1];
                    } else {
                      e.target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="500" viewBox="0 0 400 500"><rect width="400" height="500" fill="%23F4EFEA"/><text x="50%" y="48%" dominant-baseline="middle" text-anchor="middle" font-family="serif" font-size="18" fill="%23584738" letter-spacing="2">HOUSE OF KARNNIKARAA</text><text x="50%" y="54%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="11" fill="%238C7D6E" letter-spacing="1">Handcrafted Heritage</text></svg>';
                    }
                  }}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-in-out ${
                    activeImage === i ? 'opacity-100 z-10' : 'opacity-0 z-0'
                  }`}
                />
              ))}
              {/* NEW badge */}
              <span className="absolute top-4 left-4 bg-[#584738] text-[#F5E6D8] text-[9px] font-sans font-bold tracking-[0.2em] px-3 py-1.5 uppercase">
                New Arrival
              </span>
            </div>
            {/* Thumbnails (shown only if multiple views exist) */}
            {galleryImages.length > 1 && (
              <div className="flex overflow-x-auto gap-2 sm:grid sm:grid-cols-4 pb-1 scrollbar-none -mx-1 px-1 sm:mx-0 sm:px-0">
                {galleryImages.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    className={`w-16 h-16 sm:w-auto sm:aspect-square shrink-0 overflow-hidden border-2 transition-all duration-200 focus:outline-none bg-[#FAF8F5] flex items-center justify-center rounded-xs cursor-pointer ${activeImage === i ? 'border-[#584738]' : 'border-transparent hover:border-[#584738]/40'}`}
                  >
                    <img 
                      src={src} 
                      alt="" 
                      referrerPolicy="no-referrer"
                      onError={(e) => { 
                        e.target.onerror = null; 
                        e.target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" fill="%23F4EFEA"/></svg>';
                      }}
                      className="w-full h-full object-cover" 
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT: Product Info */}
          <div className="flex flex-col gap-5 pt-2">

            {/* Name */}
            <h2 className="font-serif text-2xl md:text-3xl font-normal tracking-wide text-[#584738] leading-snug">
              {product.name}
            </h2>

            {/* Rating row */}
            <div className="flex items-center gap-3">
              <Stars rating={5} />
              <span className="text-[12px] font-sans text-[#584738]/55 tracking-wide">
                {reviewsList.length} Reviews
              </span>
            </div>

            {/* Out of Stock notice or low stock banner */}
            {isOutOfStock ? (
              <div className="flex items-center gap-2.5">
                <span className="inline-block px-3 py-1 bg-[#8C3E3E] text-white text-[10px] font-sans font-bold tracking-widest uppercase">
                  Out of Stock
                </span>
                <span className="text-[11.5px] text-[#8C3E3E] font-sans font-medium">
                  Currently unavailable — check back soon
                </span>
              </div>
            ) : liveStock <= 5 ? (
              <span className="text-[11.5px] text-[#8C3E3E] font-medium font-sans">
                Only {liveStock} left in stock — order soon
              </span>
            ) : null}

            {/* Price */}
            <p className="text-xl font-sans font-bold text-[#584738] tracking-wide">
              Rs. {product.price.toLocaleString('en-IN')}.00
            </p>

            {/* Material tag */}
            <p className="text-[12px] font-sans text-[#584738]/60 border-l-2 border-[#584738]/30 pl-3 leading-relaxed">
              {product.material}
            </p>

            <div className="h-px bg-[#584738]/10" />

            {/* Qty + Add to Bag */}
            <div className="flex items-stretch gap-3">
              {/* Qty */}
              <div className={`flex items-center border border-[#584738]/30 select-none ${isOutOfStock ? 'opacity-40 pointer-events-none' : ''}`}>
                <button
                  disabled={isOutOfStock || qty <= 1}
                  onClick={() => setQty(q => Math.max(1, q - 1))}
                  className="w-10 h-full flex items-center justify-center text-lg text-[#584738] hover:bg-[#584738]/5 transition-colors focus:outline-none disabled:opacity-40"
                >-</button>
                <span className="w-10 text-center font-sans font-semibold text-[#584738] text-sm">{isOutOfStock ? 0 : qty}</span>
                <button
                  disabled={isOutOfStock}
                  onClick={() => {
                    const maxStock = liveStock;
                    setQty(q => {
                      if (q + 1 > maxStock) {
                        triggerStockLimitAlert(maxStock);
                        return maxStock;
                      }
                      return q + 1;
                    });
                  }}
                  className="w-10 h-full flex items-center justify-center text-lg text-[#584738] hover:bg-[#584738]/5 transition-colors focus:outline-none disabled:opacity-40"
                >+</button>
              </div>
              {/* Add to Bag */}
              {isOutOfStock ? (
                <button
                  disabled={true}
                  className="flex-1 bg-neutral-300 text-neutral-500 py-3.5 text-xs font-sans font-bold tracking-widest uppercase cursor-not-allowed shadow-xs"
                >
                  Out of Stock
                </button>
              ) : (
                <button
                  onClick={handleAddToCart}
                  className="flex-1 bg-[#CEC1A8] hover:bg-[#b8a990] text-[#584738] py-3.5 text-xs font-sans font-bold tracking-widest uppercase transition-colors shadow-xs"
                >
                  Add to Bag
                </button>
              )}
            </div>

            {/* Buy It Now */}
            {isOutOfStock ? (
              <button
                disabled={true}
                className="w-full bg-neutral-200 text-neutral-400 py-3.5 text-xs font-sans font-bold tracking-widest uppercase cursor-not-allowed shadow-xs"
              >
                Out of Stock
              </button>
            ) : (
              <button
                onClick={() => { handleAddToCart(); setIsCartOpen(false); setView('checkout'); }}
                className="w-full bg-[#584738] text-white hover:bg-black py-3.5 text-xs font-sans font-bold tracking-widest uppercase transition-colors shadow-xs"
              >
                Buy It Now
              </button>
            )}

            {/* Wishlist */}
            <button
              type="button"
              onClick={() => {
                if (toggleWishlist && product) toggleWishlist(product.id);
              }}
              className="flex items-center gap-2 text-[12px] font-sans text-[#584738]/70 hover:text-[#584738] transition-colors focus:outline-none self-start cursor-pointer"
            >
              <HeartIcon filled={isWishlisted} className="w-5 h-5" activeColor="#E11D48" />
              <span className={isWishlisted ? 'text-rose-600 font-semibold' : ''}>
                {isWishlisted ? 'Saved to Wishlist' : 'Add to Wishlist'}
              </span>
            </button>

            <div className="h-px bg-[#584738]/10" />

            {/* Trust block */}
            <div className="bg-[#584738]/[0.03] border border-[#584738]/8 p-4 rounded-xs">
              <p className="text-[12px] font-serif font-medium text-[#584738] mb-1 leading-relaxed">
                Trust matters when buying Fine Jewellery.<br />
                <span className="font-normal text-[#584738]/65">Let us bring the experience to you in the comfort of your home.</span>
              </p>
            </div>

            {/* Support links */}
            <div className="flex flex-col gap-2.5">
              <a href="tel:+919166209215" className="text-[11.5px] font-sans text-[#584738]/60 hover:text-[#584738] flex items-center gap-2 transition-colors">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                </svg>
                Contact us +91 91662 09215
              </a>
              <button onClick={() => setIsBookingOpen(true)} className="text-[11.5px] font-sans text-[#584738]/60 hover:text-[#584738] flex items-center gap-2 transition-colors focus:outline-none text-left">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z" />
                </svg>
                Experience in Stores
              </button>
            </div>
          </div>
        </div>

        {/* ── TABS ── */}
        <div className="border-t border-b border-[#584738]/10 mb-8">
          <div className="flex justify-center overflow-x-auto scrollbar-none">
            {tabs.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-8 py-3.5 text-[11px] font-sans font-medium tracking-[0.22em] uppercase transition-colors focus:outline-none whitespace-nowrap ${
                  activeTab === tab
                    ? 'text-[#584738] border-b-2 border-[#584738]'
                    : 'text-[#584738]/40 hover:text-[#584738]/70 border-b-2 border-transparent'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* ── TAB PANELS ── */}
        {activeTab === 'description' && (
          <div className="max-w-3xl mx-auto mb-0 px-2 md:px-0">
            {(product.description || '').split('\n\n').map((para, i) => (
              <p key={i} className="font-serif text-[16px] md:text-[17px] font-light text-[#584738]/80 leading-[1.9] tracking-wide mb-6" dangerouslySetInnerHTML={{ __html: para }} />
            ))}
          </div>
        )}

        {activeTab === 'additional information' && (
          <div className="max-w-2xl mx-auto mb-0 px-2 md:px-0">
            <table className="w-full border-collapse">
              <tbody>
                {(product.additionalInfo || []).map(({ label, value }) => (
                  <tr key={label} className="border-b border-[#584738]/8">
                    <td className="py-4 pr-8 font-sans text-[#584738]/50 font-medium text-[11px] tracking-[0.2em] uppercase w-44">{label}</td>
                    <td className="py-4 font-serif text-[15px] font-light text-[#584738]/85 tracking-wide">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'reviews' && (
          <div className="max-w-3xl mx-auto mb-0">
            {/* Overall Rating */}
            <div className="flex flex-row gap-6 items-center border-b border-[#584738]/10 pb-6 mb-6">
              <div className="flex flex-col items-center gap-1 shrink-0 min-w-[80px]">
                <span className="text-5xl font-serif font-semibold text-[#584738]">5</span>
                <span className="text-xs font-sans text-[#584738]/50 uppercase tracking-widest">out of 5</span>
                <Stars rating={5} size="md" />
                <span className="text-[11px] text-[#584738]/40 font-sans">{reviewsList.length} Reviews</span>
              </div>
              <div className="flex flex-col gap-2 flex-1 w-full">
                {[['5 Stars', 92], ['4 Stars', 6], ['3 Stars', 1], ['2 Stars', 0.5], ['1 Stars', 0.5]].map(([label, pct]) => (
                  <RatingBar key={label} label={label} pct={pct} />
                ))}
              </div>
            </div>

            {/* Write review button & form */}
            <div className="flex flex-col items-center mb-6">
              <button
                onClick={() => {
                  if (!currentUser) {
                    alert('Please sign in to write a review.');
                    setIsAuthOpen(true);
                  } else {
                    setShowWriteForm(prev => !prev);
                  }
                }}
                className="border border-[#584738]/25 hover:bg-[#584738]/5 px-8 py-2.5 text-[11px] font-sans font-bold tracking-widest uppercase transition-colors text-[#584738] flex items-center gap-2 focus:outline-none cursor-pointer"
              >
                {showWriteForm ? 'Close Form' : 'Write a product review'}
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" />
                </svg>
              </button>

              {showWriteForm && (
                <form
                  onSubmit={handleAddReviewSubmit}
                  className="mt-6 p-5 bg-[#584738]/[0.02] border border-[#584738]/10 rounded-sm flex flex-col gap-4 font-sans text-left animate-fade-in w-full max-w-xl mx-auto"
                >
                  <h4 className="font-serif text-[15px] tracking-wider font-semibold text-[#584738] uppercase">Write A Customer Review</h4>
                  
                  {/* Name field: disabled and prefilled to Aman */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-[#584738]/60">Your Name</label>
                    <input
                      type="text"
                      value={currentUser ? (currentUser.displayName || currentUser.email?.split('@')[0] || 'Client') : ''}
                      disabled
                      className="bg-transparent border border-[#584738]/20 px-3 py-2 text-sm text-[#584738]/60 cursor-not-allowed select-none outline-none font-medium"
                    />
                  </div>

                  {/* Star rating selection */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-[#584738]/60">Your Rating</label>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {[1, 2, 3, 4, 5].map((starNum) => (
                        <button
                          key={starNum}
                          type="button"
                          onClick={() => setNewReviewRating(starNum)}
                          className="focus:outline-none cursor-pointer p-0.5 hover:scale-110 transition-transform duration-200"
                        >
                          <svg
                            className={`w-6 h-6 transition-colors duration-200 ${starNum <= newReviewRating ? 'text-amber-500' : 'text-[#584738]/20'}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Textarea */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-[#584738]/60">Review Content</label>
                    <textarea
                      value={newReviewText}
                      onChange={(e) => setNewReviewText(e.target.value)}
                      placeholder="Tell us about your experience with this beautiful jewelry..."
                      rows="4"
                      required
                      className="bg-transparent border border-[#584738]/25 px-3 py-2 text-sm text-[#584738] placeholder-[#584738]/40 focus:outline-none focus:border-[#584738] rounded-none"
                    />
                  </div>

                  {/* Mock review image checkbox */}
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="checkbox"
                      id="attach-image-mock"
                      checked={attachMockImage}
                      onChange={(e) => setAttachMockImage(e.target.checked)}
                      className="accent-[#584738] w-4 h-4 cursor-pointer"
                    />
                    <label htmlFor="attach-image-mock" className="text-[11.5px] font-medium text-[#584738]/85 cursor-pointer select-none">
                      Attach client review images (mockup gallery)
                    </label>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-3 mt-3">
                    <button
                      type="submit"
                      className="bg-[#584738] hover:bg-black text-[#F5E6D8] px-6 py-2.5 text-[11px] font-sans font-bold tracking-widest uppercase transition-colors shadow-xs cursor-pointer"
                    >
                      Submit Review
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowWriteForm(false)}
                      className="border border-[#584738]/20 hover:bg-[#584738]/5 text-[#584738] px-6 py-2.5 text-[11px] font-sans font-bold tracking-widest uppercase transition-colors cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Review list header */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[11px] font-sans font-bold uppercase tracking-widest text-[#584738]">Review List</p>
                <p className="text-[11px] font-sans text-[#584738]/45 mt-0.5">
                  Showing {Math.min(visibleReviewsCount, reviewsList.length)} of {reviewsList.length}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-sans text-[#584738]/50 uppercase tracking-wider">Sort by:</span>
                <select
                  value={reviewSort}
                  onChange={e => setReviewSort(e.target.value)}
                  className="text-[11px] font-sans text-[#584738] border border-[#584738]/20 px-2.5 py-1.5 focus:outline-none bg-transparent cursor-pointer"
                >
                  <option value="latest">Latest</option>
                  <option value="top">Top Rated</option>
                  <option value="low">Lowest Rated</option>
                </select>
              </div>
            </div>

            {/* Reviews List */}
            <div className="flex flex-col divide-y divide-[#584738]/5">
              {reviewsList.slice(0, visibleReviewsCount).map((r, i) => (
                <ReviewCard key={i} {...r} onImageClick={setActiveLightboxImage} />
              ))}
            </div>

            {/* Inline Load More button */}
            {visibleReviewsCount < reviewsList.length ? (
              <button
                onClick={() => setVisibleReviewsCount(prev => Math.min(prev + 3, reviewsList.length))}
                className="mt-8 w-full border border-[#584738]/20 hover:bg-[#584738]/5 py-3.5 text-[11px] font-sans font-semibold tracking-widest uppercase text-[#584738] transition-colors focus:outline-none cursor-pointer"
              >
                More Reviews
              </button>
            ) : (
              <p className="mt-8 text-center text-xs font-sans text-[#584738]/40 font-medium tracking-wide">
                All customer reviews have been loaded.
              </p>
            )}
          </div>
        )}

        {/* All New Arrivals Grid */}
        {sortedArrivals.length > 0 && (
          <div className="mt-16 md:mt-24 pt-12 border-t border-[#584738]/10">
            <div className="text-center mb-8 md:mb-12">
              <span className="text-[10px] uppercase font-sans tracking-[0.25em] text-[#584738]/60 font-semibold block mb-2">Curated Additions</span>
              <h3 className="font-serif text-2xl md:text-3xl text-[#584738] font-normal tracking-wide">All New Arrivals</h3>
              <p className="font-sans text-xs md:text-sm text-[#584738]/70 mt-2 max-w-md mx-auto">Explore all our newest handcrafted creations, freshly added to the collection.</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-4 md:gap-6">
              {sortedArrivals.map((prod, idx) => {
                const isItemWishlisted = (wishlist || []).some(id => String(id) === String(prod.id));
                const itemImg = resolveMediaUrl(prod.image || prod.images?.[0] || '');
                return (
                  <div key={prod.id || idx} className="flex flex-col group bg-white border border-[#584738]/10 p-2 sm:p-2.5 md:p-3 rounded-xs shadow-xs hover:shadow-md transition-shadow duration-300">
                    <div className="relative aspect-[4/5] bg-[#FAF8F5] overflow-hidden cursor-pointer mb-2.5 rounded-xs">
                      {/* Wishlist button */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (toggleWishlist) toggleWishlist(prod.id);
                        }}
                        title={isItemWishlisted ? "Remove from Wishlist" : "Add to Wishlist"}
                        className={`absolute top-2 right-2 z-20 w-8 h-8 rounded-full flex items-center justify-center shadow-xs transition-all duration-300 ease-out active:scale-90 hover:scale-110 cursor-pointer touch-manipulation ${
                          isItemWishlisted 
                            ? 'bg-rose-50 text-rose-600 border border-rose-200' 
                            : 'bg-white/95 hover:bg-white text-[#584738]/70 hover:text-[#584738] border border-[#584738]/15'
                        }`}
                      >
                        <HeartIcon filled={isItemWishlisted} className="w-4 h-4" activeColor="#E11D48" />
                      </button>
                      <div 
                        onClick={() => {
                          if (setSelectedProduct) setSelectedProduct(prod);
                          setView('product');
                        }}
                        className="w-full h-full"
                      >
                        <img
                          src={itemImg}
                          alt={prod.name}
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            e.target.onerror = null;
                            const alt = (prod.images || []).map(resolveMediaUrl).find(u => u && u !== e.target.src);
                            if (alt) e.target.src = alt;
                            else e.target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="500" viewBox="0 0 400 500"><rect width="400" height="500" fill="%23F4EFEA"/><text x="50%" y="48%" dominant-baseline="middle" text-anchor="middle" font-family="serif" font-size="18" fill="%23584738" letter-spacing="2">HOUSE OF KARNNIKARAA</text><text x="50%" y="54%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="11" fill="%238C7D6E" letter-spacing="1">Handcrafted Heritage</text></svg>';
                          }}
                          className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                    </div>
                    <div className="text-center px-1">
                      <p className="text-[#584738] font-serif text-xs md:text-sm truncate font-medium">{prod.name}</p>
                      <p className="text-[#584738]/75 font-sans text-xs mt-0.5">₹{(prod.price || 0).toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {/* ── FOOTER ── */}
      <Footer setView={setView} />

      {/* ── MENU DRAWER ── */}
      <MenuDrawer isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} setView={setView} setIsBookingOpen={setIsBookingOpen} currentUser={currentUser} setCurrentUser={setCurrentUser} setIsAuthOpen={setIsAuthOpen} wishlist={wishlist} />
      <AuthModal isOpen={isAuthOpen} onClose={() => setIsAuthOpen(false)} currentUser={currentUser} setCurrentUser={setCurrentUser} />

      {/* ── CART SLIDE-OVER ── */}
      <div className={`fixed inset-x-0 bottom-0 top-[70px] md:top-[75px] z-50 transition-opacity duration-300 pointer-events-none ${isCartOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0'}`}>
        <div onClick={() => setIsCartOpen(false)} className="absolute inset-0 bg-black/40 backdrop-blur-xs" />
        <div className={`fixed inset-y-0 right-0 h-full w-full md:w-96 bg-noise shadow-2xl p-6 transition-transform duration-300 flex flex-col ${isCartOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex items-center justify-between border-b border-[#584738]/10 pb-4 mb-6 flex-shrink-0">
            <h3 className="font-serif text-lg tracking-widest font-semibold uppercase flex items-center gap-2 text-[#584738]">
              Your Cart
              <span className="text-xs bg-[#584738]/10 px-2.5 py-0.5 rounded-full font-sans font-bold">{cartItems.reduce((a, i) => a + i.qty, 0)}</span>
            </h3>
            <button onClick={() => setIsCartOpen(false)} className="focus:outline-none" aria-label="Close Cart"><CloseIcon /></button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto space-y-5 pr-1 scrollbar-thin">
            {cartItems.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-6 text-[#584738]/40 font-serif">
                <CartIcon />
                <p className="mt-3 text-sm tracking-wide font-medium">Your cart is empty.</p>
              </div>
            ) : (
              cartItems.map((item, idx) => (
                <div key={`${item.id}-${idx}`} className="flex gap-4 border-b border-[#584738]/5 pb-4 font-serif">
                  <img src={item.image} alt={item.name} className="w-16 h-20 object-cover border border-[#584738]/5" />
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h4 className="text-sm font-serif font-medium tracking-wide text-[#584738]/90 leading-tight">{item.name}</h4>
                      <p className="text-[12.5px] text-[#584738] font-medium mt-1">
                        ₹{item.price.toLocaleString('en-IN')} <span className="text-[9.5px] text-[#584738]/60 font-sans font-normal">(incl. of all taxes)</span>
                      </p>
                    </div>
                    <div className="flex items-center justify-between text-xs mt-2 font-sans">
                      <div className="flex items-center border border-[#584738]/20 select-none">
                        <button onClick={() => handleQtyChange(item.id, -1)} className="px-2 py-0.5 hover:bg-[#584738]/5 focus:outline-none">-</button>
                        <span className="px-3 py-0.5 text-center font-medium">{item.qty}</span>
                        <button onClick={() => handleQtyChange(item.id, 1)} className="px-2 py-0.5 hover:bg-[#584738]/5 focus:outline-none">+</button>
                      </div>
                      <button onClick={() => removeItem(item.id)} className="text-[#584738]/60 hover:text-red-500 font-semibold">Remove</button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          {cartItems.length > 0 && (
            <div className="border-t border-[#584738]/10 pt-6 mt-6 flex flex-col gap-4 flex-shrink-0">
              <div className="flex items-center justify-between font-serif">
                <div className="flex flex-col text-left">
                  <span className="text-md font-medium tracking-wide text-[#584738]/80">Subtotal</span>
                  <span className="text-[10px] text-[#584738]/60 font-sans normal-case">(Inclusive of all taxes)</span>
                </div>
                <span className="text-lg font-bold text-[#584738]">₹{getSubtotal().toLocaleString('en-IN')}</span>
              </div>
              <p className="text-[10px] text-[#584738]/60 tracking-wider text-center font-sans">Complimentary Express Shipping • (Inclusive of all taxes)</p>
              <button onClick={() => { setIsCartOpen(false); setView('checkout'); }} className="w-full bg-[#584738] text-white hover:bg-black py-3.5 text-xs tracking-widest font-bold uppercase transition-colors shadow-xs font-sans">
                PROCEED TO CHECKOUT
              </button>
            </div>
          )}

          {/* ── UNDO TOAST ── */}
          {undoItem && (
            <div className="flex-shrink-0 mt-3 flex items-center justify-between gap-3 bg-[#584738] text-[#F5E6D8] px-4 py-3 rounded-xs animate-fade-in">
              <p className="text-[11.5px] font-sans tracking-wide truncate flex-1">
                <span className="opacity-60 mr-1">Removed:</span>{undoItem.name}
              </p>
              <button
                onClick={handleUndo}
                className="flex items-center gap-1.5 text-[11px] font-sans font-bold tracking-widest uppercase text-[#CEC1A8] hover:text-white transition-colors focus:outline-none flex-shrink-0"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                </svg>
                Undo
              </button>
            </div>
          )}
        </div>
      </div>


      {/* ── IMAGE LIGHTBOX MODAL ── */}
      <div
        className={`fixed inset-0 z-60 transition-opacity duration-300 flex items-center justify-center pointer-events-none ${
          activeLightboxImage ? 'opacity-100 pointer-events-auto' : 'opacity-0'
        }`}
      >
        {/* Backdrop blur overlay */}
        <div
          onClick={() => setActiveLightboxImage(null)}
          className="absolute inset-0 bg-black/85 backdrop-blur-md transition-opacity duration-300"
        />

        {/* Lightbox Content Container */}
        <div className="relative z-10 max-w-4xl max-h-[90vh] mx-4 flex flex-col items-center">
          {/* Close button */}
          <button
            onClick={() => setActiveLightboxImage(null)}
            className="absolute -top-12 right-0 md:-right-12 text-[#F5E6D8] hover:text-white transition-colors p-2 focus:outline-none cursor-pointer"
            aria-label="Close Lightbox"
          >
            <svg className="w-8 h-8 stroke-current" fill="none" viewBox="0 0 24 24" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Centered Image */}
          {activeLightboxImage && (
            <img
              src={activeLightboxImage}
              alt="Enlarged review detail"
              className="max-w-full max-h-[80vh] object-contain border border-[#F5E6D8]/20 shadow-2xl rounded-xs select-none transition-transform duration-500 scale-100 animate-zoom-in"
            />
          )}
          
          <p className="text-white/60 text-xs font-sans mt-3 tracking-wider">
            Click anywhere outside or press Close to return.
          </p>
        </div>
      </div>

    </div>
  );
}
